// ===================================================
// config/navigation.registry.js
// Единый SSOT навигации app.markbase.ru (hubs + items).
// Потребители: Sidebar rail/flyout, FavoritePagesTabs, MenuGuide.
// ===================================================
import { isHrefVisibleWithActiveModules } from '../utils/activeModulesHrefFilter';
import {
  DEFAULT_NAV_PRESET_ID,
  getSystemPreset,
} from './navigation.presets';
import { isPlatformAdminCompanyId } from '../utils/systemCompany';
/**
 * @typedef {object} NavItem
 * @property {string} key
 * @property {string} href
 * @property {string} label
 * @property {string} iconKey
 * @property {boolean} [show]
 * @property {boolean} [disabled]
 * @property {boolean} [external]
 */

/**
 * @typedef {object} NavHub
 * @property {string} id
 * @property {string} label
 * @property {string} title
 * @property {string} [sub]
 * @property {string} iconKey
 * @property {NavItem[]} items
 * @property {string[]} defaultQuickPicks
 */

const item = (href, label, iconKey, opts = {}) => ({
  key: opts.key || href,
  href,
  label,
  iconKey,
  show: opts.show !== false,
  disabled: Boolean(opts.disabled),
  external: Boolean(opts.external),
  badge: opts.badge === 'new' || opts.badge === 'hot' ? opts.badge : undefined,
  ...opts,
});

/**
 * Собирает хабы с учётом прав / компании / модулей.
 * @param {object} ctx
 * @param {object} ctx.urls
 * @param {object} ctx.permissions — { has, isSuperAdmin, PERMISSIONS }
 * @param {object} ctx.flags — { hasCompany, isCompanyInactive, isVerifiedCompany, companyId }
 * @param {string[]} ctx.activeModules
 * @returns {NavHub[]}
 */
export function buildNavigationHubs({
  urls = {},
  permissions = {},
  flags = {},
  activeModules,
} = {}) {
  const has = permissions.has || (() => false);
  const isSuperAdmin = permissions.isSuperAdmin || (() => false);
  const PERMISSIONS = permissions.PERMISSIONS || {};
  const { hasCompany, isCompanyInactive, isVerifiedCompany } = flags;
  const companyId = flags.companyId || null;
  const isSystemMarkbaseCompany = Boolean(flags.isSystemMarkbaseCompany);
  const isPlatformAdminCompany = isPlatformAdminCompanyId(companyId);
  // Server ACL (/access/me) or fallback: SA + ООО|ИП
  const concurensAllowed = flags.concurensAllowed != null
    ? Boolean(flags.concurensAllowed)
    : isSystemMarkbaseCompany;

  const {
    USERS_PORTAL_URL,
    WALLET_URL,
    BILLING_URL,
    SCHEDULER_DASHBOARD_URL,
    BACKUP_DASHBOARD_URL,
    DELGRAM_URL,
    MARKET_MODULE_URL,
    MARKET_CONNECTION_URL,
    MPLAZA_APP_CONNECTION_URL,
    MPLAZA_SELLER_CONNECTION_URL,
    CORE_MONITORING_DASHBOARD_URL,
  } = urls;

  const inactive = Boolean(isCompanyInactive);

  /** @type {NavHub[]} */
  const hubs = [
    {
      id: 'main',
      label: 'Главное',
      title: 'Главное',
      sub: 'Рабочий стол и коммуникации',
      iconKey: 'dashboard',
      defaultQuickPicks: ['/dashboard', '/workspace', '/chats'],
      items: [
        item('/menu-guide', 'Меню', 'appstore', { show: true }),
        item('/workspace', 'Рабочий стол', 'team', { show: hasCompany, disabled: inactive }),
        item('/dashboard', 'Дашборд', 'dashboard', { show: hasCompany, disabled: inactive }),
        item('/chat', 'AI-помощник (ВЭЙГПТ)', 'robot', { show: hasCompany, disabled: inactive }),
        item('/chats', 'Чаты клиентов', 'messageSquare', { show: hasCompany, disabled: inactive }),
      ],
    },
    {
      id: 'products',
      label: 'Каталог',
      title: 'Каталог',
      sub: 'Товары, цены, поставщики, импорт',
      iconKey: 'shopping',
      defaultQuickPicks: ['/products', '/settings/prices', '/suppliers'],
      items: [
        item('/products', 'Каталог', 'shopping', {
          show: has(PERMISSIONS.PRODUCTS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/settings/prices', 'Цены', 'ruble', {
          show: has(PERMISSIONS.SETTINGS_VIEW),
          disabled: inactive,
        }),
        item('/products/composite', 'Составные товары', 'apartment', {
          show: has(PERMISSIONS.PRODUCTS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/product-groups', 'Группы товаров', 'partition', {
          show: has(PERMISSIONS.PRODUCT_GROUPS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/dictionaries', 'Справочники', 'book', { show: hasCompany }),
        item('/dictionaries/brand-discounts', 'Скидки по брендам', 'percentage', {
          show: has(PERMISSIONS.SUPPLIERS_UPDATE) && hasCompany,
          disabled: inactive,
        }),
        item('/images-management', 'Медиафайлы', 'picture', {
          show: has(PERMISSIONS.PRODUCTS_UPDATE) && hasCompany,
          disabled: inactive,
        }),
        item('/suppliers', 'Поставщики', 'truck', {
          show: has(PERMISSIONS.SUPPLIERS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/automations', 'Автоматизации', 'clock', {
          show: has(PERMISSIONS.SUPPLIERS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/file-import', 'Импорт файлов', 'cloudDownload', {
          show: has(PERMISSIONS.FILE_IMPORT_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/catalog-exports', 'Экспорт и фиды', 'cloudUpload', {
          show: has(PERMISSIONS.PRODUCTS_EXPORT) && hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'sales',
      label: 'Продажи',
      title: 'Продажи',
      sub: 'Заказы, маркетплейсы, B2B',
      iconKey: 'cart',
      defaultQuickPicks: ['/ops', '/modules/orders', '/connections?path=/admin/marketplace', '/commerce-hub'],
      items: [
        item('/ops', 'Операционный центр', 'dashboard', {
          show: has(PERMISSIONS.ORDERS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/commerce-hub', 'Мастер настройки', 'shop', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/orders', 'Заказы', 'cart', {
          show: has(PERMISSIONS.ORDERS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/connections?path=/admin/marketplace', 'Маркетплейсы', 'shop', {
          key: '/connections?path=/admin/marketplace',
          show: has(PERMISSIONS.MARKETPLACES_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item(MARKET_MODULE_URL, 'B2B закупки', 'truck', {
          show: Boolean(MARKET_MODULE_URL) && has(PERMISSIONS.ORDERS_VIEW) && hasCompany,
          disabled: inactive,
          external: true,
        }),
        item(MARKET_CONNECTION_URL, 'B2B — подключение', 'link', {
          show: Boolean(MARKET_CONNECTION_URL) && has(PERMISSIONS.ORDERS_VIEW) && hasCompany,
          disabled: inactive,
          external: true,
        }),
      ],
    },
    {
      id: 'warehouse',
      label: 'Склад',
      title: 'Склад и остатки',
      sub: 'Склады, WMS, маркировка',
      iconKey: 'home',
      defaultQuickPicks: ['/warehouses', '/warehouses/manager', '/modules/wms'],
      items: [
        item('/warehouses', 'Склады', 'home', {
          show: has(PERMISSIONS.WAREHOUSES_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/warehouses/manager', 'Остатки и синхронизация', 'sync', {
          show: has(PERMISSIONS.WAREHOUSES_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/warehouses/ulr-operations', 'Операции ULR', 'truck', {
          show: has(PERMISSIONS.WAREHOUSES_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/modules/wms', 'Склад WMS — планы и задачи', 'appstore', {
          show: has(PERMISSIONS.WAREHOUSES_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/expiry-settings', 'Срок годности', 'calendar', {
          show: has(PERMISSIONS.PRODUCTS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/marking', 'Честный ЗНАК', 'qrcode', {
          show: hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'documents',
      label: 'Документы',
      title: 'Документооборот',
      iconKey: 'fileText',
      defaultQuickPicks: ['/documents', '/documents/tasks', '/documents/incoming'],
      items: [
        item('/documents', 'Документы', 'fileText', {
          show: has(PERMISSIONS.DOCUMENTS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/documents/tasks', 'Задачи', 'fileText', {
          show: has(PERMISSIONS.DOCUMENTS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/documents/incoming', 'Входящие УПД', 'fileText', {
          show: has(PERMISSIONS.DOCUMENTS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/documents/stock-pieces', 'Куски товаров', 'scissor', {
          show: has(PERMISSIONS.DOCUMENTS_MANAGE) && hasCompany,
          disabled: inactive,
        }),
        item('/documents/settings', 'Настройки', 'settings', {
          show: has(PERMISSIONS.DOCUMENTS_MANAGE) && hasCompany,
          disabled: inactive,
        }),
        item('/documents/settings/connection', 'Подключение', 'link', {
          show: has(PERMISSIONS.DOCUMENTS_MANAGE) && hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'finance',
      label: 'Финансы',
      title: 'Финансы',
      iconKey: 'ruble',
      defaultQuickPicks: ['/finance-overview', WALLET_URL, BILLING_URL].filter(Boolean),
      items: [
        item('/finance-overview', 'Сводка', 'appstore', { show: hasCompany }),
        item(WALLET_URL, 'Баланс и платежи', 'ruble', {
          show: Boolean(WALLET_URL) && hasCompany,
          external: true,
        }),
        item(BILLING_URL, 'Тарифы и биллинг', 'percentage', {
          show: Boolean(BILLING_URL) && hasCompany,
          external: true,
        }),
        item('/cost-details', 'Детализация', 'barChart', { show: hasCompany }),
      ],
    },
    {
      id: 'analytics',
      label: 'Аналитика',
      title: 'Аналитика',
      iconKey: 'barChart',
      defaultQuickPicks: ['/analytics', '/analytics/settings'],
      items: [
        item('/analytics', 'Аналитика', 'barChart', {
          show: has(PERMISSIONS.ANALYTICS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/analytics/settings', 'Настройки аналитики', 'settings', {
          show: has(PERMISSIONS.ANALYTICS_ADMIN) && hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'kassa',
      label: 'Касса',
      title: 'Касса',
      iconKey: 'shop',
      defaultQuickPicks: ['/kassa/pos', '/modules/kassa', '/kassa/reports'],
      items: [
        item('/kassa/pos', 'Касса (POS)', 'shop', {
          show: has(PERMISSIONS.KASSA_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/modules/kassa', 'Касса (веб-модуль)', 'shop', {
          show: has(PERMISSIONS.KASSA_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/kassa/reports', 'Отчёты кассы', 'fileText', {
          show: (has(PERMISSIONS.KASSA_REPORTS) || has(PERMISSIONS.KASSA_VIEW)) && hasCompany,
          disabled: inactive,
        }),
        item('/kassa/admin', 'Управление кассами', 'settings', {
          show: has(PERMISSIONS.KASSA_MANAGE) && hasCompany,
          disabled: inactive,
        }),
        item('/kassa/admin/connection', 'Подключение', 'link', {
          show: has(PERMISSIONS.KASSA_MANAGE) && hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'integrations',
      label: 'Интеграции',
      title: 'Интеграции',
      iconKey: 'api',
      defaultQuickPicks: ['/connections', '/api-keys', '/exchange-log'],
      items: [
        item('/connections', 'Подключения', 'api', {
          show: (has(PERMISSIONS.MARKETPLACES_VIEW) || has(PERMISSIONS.SETTINGS_VIEW) || has(PERMISSIONS.SYNC_VIEW_LOGS)) && hasCompany,
          disabled: inactive,
        }),
        item('/api-keys', 'Ключи API', 'key', {
          show: has(PERMISSIONS.SETTINGS_VIEW) && hasCompany && isVerifiedCompany,
          disabled: inactive,
        }),
        item('/exchange-log', 'Журнал обменов', 'sync', {
          show: (has(PERMISSIONS.SYNC_VIEW_LOGS) || has(PERMISSIONS.SYNC_EXECUTE) || has(PERMISSIONS.MARKETPLACES_VIEW) || has(PERMISSIONS.SETTINGS_VIEW)) && hasCompany,
          disabled: inactive,
        }),
        item('/modules/integrations-admin', 'Кабинет интеграций', 'api', {
          show: (has(PERMISSIONS.MARKETPLACES_VIEW) || has(PERMISSIONS.SETTINGS_VIEW)) && hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'modules',
      label: 'Модули',
      title: 'Модули экосистемы',
      iconKey: 'appstore',
      defaultQuickPicks: ['/modules/shop', '/modules/crm', '/modules/delivery'],
      items: [
        item('/modules/delivery', 'Доставка', 'thunder', { show: hasCompany, disabled: inactive }),
        item('/modules/logistics', 'Логистика', 'truck', { show: hasCompany, disabled: inactive }),
        item('/modules/crm', 'CRM — клиенты B2B/B2C', 'team', { show: hasCompany, disabled: inactive }),
        item('/modules/hrm', 'HRM Персонал', 'user', { show: hasCompany, disabled: inactive }),
        item('/modules/files', 'Файлы и медиа', 'folder', { show: hasCompany, disabled: inactive }),
        item('/modules/search', 'SEARCH', 'search', { show: hasCompany, disabled: inactive }),
        item('/modules/bonus', 'Бонусная система', 'percentage', { show: hasCompany, disabled: inactive }),
        item('/modules/shop', 'Shop: витрина и настройки', 'shop', { show: hasCompany, disabled: inactive }),
        item('/modules/company-services-booking', 'Услуги: календарь и CRM', 'calendar', {
          show: hasCompany,
          disabled: inactive,
        }),
        item(DELGRAM_URL, 'Делграм', 'shop', {
          show: Boolean(DELGRAM_URL),
          external: true,
        }),
        item(MPLAZA_APP_CONNECTION_URL, 'MPLAZA — подключение', 'link', {
          show: Boolean(MPLAZA_APP_CONNECTION_URL) && hasCompany,
          disabled: inactive,
        }),
        item(MPLAZA_SELLER_CONNECTION_URL, 'Делграм — seller API', 'link', {
          show: Boolean(MPLAZA_SELLER_CONNECTION_URL) && hasCompany,
          disabled: inactive,
          external: true,
        }),
        item('/modules/feed', 'Молвинка', 'rss', { show: true }),
        item('/modules/torg', 'TORG: тендеры и закупки', 'barChart', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/torg/connection', 'TORG — подключение', 'link', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/finance', 'Финансы (модуль)', 'ruble', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/widgets', 'Виджеты (конструктор)', 'barChart', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/power', 'Производство (POWER)', 'thunder', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/legal', 'Юридический отдел (LEGAL)', 'fileText', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/support', 'Поддержка', 'messageSquare', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/waygen', 'ВЭЙГЕН — контент', 'robot', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/modules/services', 'Маркетплейс услуг', 'team', {
          show: hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'management',
      label: 'Компания',
      title: 'Управление компанией',
      iconKey: 'team',
      defaultQuickPicks: ['/users', '/notifications', '/our-company'],
      items: [
        item('/users', 'Пользователи', 'team', {
          show: has(PERMISSIONS.USERS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item(USERS_PORTAL_URL, 'Пользователи платформы (UAM)', 'team', {
          show: Boolean(USERS_PORTAL_URL) && isSuperAdmin() && !hasCompany,
          external: true,
        }),
        item('/notifications', 'Уведомления', 'bell', { show: true }),
        item('/api-keys', 'Ключи API', 'key', {
          show: has(PERMISSIONS.SETTINGS_VIEW) && hasCompany && isVerifiedCompany,
          disabled: inactive,
        }),
        item('/settings/currencies', 'Валюты', 'dollar', {
          show: has(PERMISSIONS.SETTINGS_VIEW),
          disabled: inactive,
        }),
        item('/our-company', 'Наша компания', 'bank', {
          show: has(PERMISSIONS.SETTINGS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/our-company?tab=branches', 'Филиалы', 'apartment', {
          key: '/our-company?tab=branches',
          show: has(PERMISSIONS.SETTINGS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/compliance', 'Соответствие', 'compliance', {
          show: has(PERMISSIONS.SETTINGS_VIEW) && hasCompany,
          disabled: inactive,
        }),
        item('/compliance/connection', 'Соответствие — подключение', 'link', {
          show: has(PERMISSIONS.SETTINGS_VIEW) && hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'neuro',
      label: 'Нейроуправление',
      title: 'Нейроуправление',
      sub: 'Companion, токены, настройки ИИ',
      iconKey: 'robot',
      defaultQuickPicks: ['/neuro/operator', '/settings/waygpt-control', '/neuro/history'],
      items: [
        item('/neuro/operator', 'Чат / Operator', 'robot', {
          show: hasCompany,
          disabled: inactive,
          badge: 'new',
        }),
        item('/neuro/tasks', 'Задачи', 'schedule', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/settings/waygpt-control', 'Настройки нейро', 'settings', {
          show: (has(PERMISSIONS.SETTINGS_VIEW) || has(PERMISSIONS.ADMIN_ACCESS)) && hasCompany,
          disabled: inactive,
        }),
        item('/settings/waygpt-control?packs=1', 'Кошелёк / токены', 'ruble', {
          key: '/settings/waygpt-control?packs=1',
          show: (has(PERMISSIONS.SETTINGS_VIEW) || has(PERMISSIONS.ADMIN_ACCESS)) && hasCompany,
          disabled: inactive,
        }),
        item('/neuro/history', 'История действий', 'logs', {
          show: hasCompany,
          disabled: inactive,
        }),
        item('/neuro/guide', 'Справка', 'appstore', {
          show: hasCompany,
          disabled: inactive,
        }),
      ],
    },
    {
      id: 'cabinet',
      label: 'Кабинет',
      title: 'Личный кабинет',
      iconKey: 'user',
      defaultQuickPicks: ['/cabinet/loyalty', '/cabinet/poa'],
      items: [
        item('/cabinet/loyalty', 'Моя карта лояльности', 'qrcode', { show: true }),
        item('/cabinet/poa', 'Электронные доверенности', 'safetyCertificate', { show: true }),
      ],
    },
  ];

  if (isSuperAdmin()) {
    hubs.push({
      id: 'admin',
      label: 'Админ',
      title: 'Администрирование',
      sub: 'Только platform admin',
      iconKey: 'safetyCertificate',
      defaultQuickPicks: ['/admin/companies', '/admin/control-plane', '/admin/logcenter'],
      items: [
        item('/admin/companies', 'Компании', 'team', { show: true }),
        item('/admin/seo', 'SEO', 'global', { show: true }),
        item('/admin/billing-management', 'Биллинг', 'percentage', { show: true }),
        item('/admin/notifications', 'Уведомления', 'bell', { show: true }),
        item('/admin/pricing-templates', 'Шаблоны цен', 'settings', { show: true }),
        item('/admin/pricing-rules', 'Правила цен', 'percentage', { show: true }),
        item('/admin/resource-styles', 'Стили', 'bgColors', { show: true }),
        item(SCHEDULER_DASHBOARD_URL, 'Планировщик (raw)', 'schedule', {
          show: Boolean(SCHEDULER_DASHBOARD_URL),
          external: true,
        }),
        item('/admin/background-jobs', 'Фоновые задачи', 'schedule', { show: true }),
        item('/admin/loyalty-settings', 'Лояльность (QR)', 'settings', { show: true }),
        item('/admin/navigation-nav', 'Навигация меню', 'control', { show: true, badge: 'new' }),
        item('/admin/database', 'БД', 'database', { show: true }),
        item(BACKUP_DASHBOARD_URL, 'Бэкапы', 'cloudUpload', {
          show: Boolean(BACKUP_DASHBOARD_URL),
          external: true,
        }),
        item('/admin/security', 'Безопасность', 'safetyCertificate', { show: true }),
        item('/admin/requisites', 'Соглашения и документы (SSOT)', 'bank', { show: true }),
        item('/admin/api-rate-limits', 'Лимиты API', 'api', { show: true }),
        item('/admin/container-resources', 'Ресурсы Docker', 'server', { show: true }),
        item('/modules/search', 'SEARCH: проекты поиска', 'search', {
          key: '/admin/modules/search',
          show: hasCompany,
          disabled: inactive,
        }),
        item('/admin/logcenter', 'LogCenter', 'logs', { show: true }),
        item('/admin/concurens/map', 'Конкуренты', 'global', {
          show: concurensAllowed,
        }),
        item('/admin/concurens/settings', 'Конкуренты — настройки', 'settings', {
          show: concurensAllowed,
        }),
        item('/admin/logcenter/connection', 'LogCenter — подключение', 'link', { show: true }),
        item('/admin/registry', 'Registry (CORE)', 'database', { show: true }),
        item('/admin/billing-management?tab=modules', 'Тарифы модулей', 'percentage', {
          key: '/admin/billing-management?tab=modules',
          show: true,
        }),
        item('/admin/plugin-hub', 'Plugin Hub', 'apartment', { show: true }),
        item('/admin/plugin-hub/connection', 'Plugin Hub — подключение', 'link', { show: true }),
        item('/admin/process-center', 'Process Center', 'schedule', { show: true }),
        item('/admin/process-center/connection', 'Process Center — подключение', 'link', { show: true }),
        item('/admin/docker-agent/connection', 'Docker Agent — подключение', 'link', { show: true }),
        item('/admin/monitoring/connection', 'Monitoring — подключение', 'link', { show: true }),
        item(CORE_MONITORING_DASHBOARD_URL, 'CORE Monitoring (hot)', 'barChart', {
          show: Boolean(CORE_MONITORING_DASHBOARD_URL),
          external: true,
        }),
        item('/admin/control-plane', 'Control Plane', 'control', { show: true }),
      ],
    });

    // Platform AI admin — only when active company is ООО (platform contour)
    if (isPlatformAdminCompany) {
      hubs.push({
        id: 'neuro-platform',
        label: 'Нейро · платформа',
        title: 'Нейроуправление — платформа',
        sub: 'Доступы и CMS (только ООО)',
        iconKey: 'robot',
        defaultQuickPicks: ['/settings/waygpt-control', '/admin/companion'],
        items: [
          item('/settings/waygpt-control', 'Настройки AI / ВЭЙГПТ', 'robot', {
            key: '/admin/neuro/waygpt-control',
            show: true,
          }),
          item('/settings/waygpt-control?tab=assistant', 'Платформенный ассистент', 'settings', {
            key: '/admin/neuro/assistant',
            show: true,
          }),
          item('/admin/companion', 'WaySen Companion CMS', 'robot', { show: true }),
        ],
      });
    }
  } else if (concurensAllowed) {
    // Allowlist users (не SA): только карта конкурентов, без полной админки.
    hubs.push({
      id: 'concurens',
      label: 'Конкуренты',
      title: 'Карта конкурентов',
      sub: 'Доступ по политике платформы',
      iconKey: 'global',
      defaultQuickPicks: ['/admin/concurens/map'],
      items: [
        item('/admin/concurens/map', 'Карта конкурентов', 'global', { show: true }),
        item('/admin/concurens/settings', 'Настройки', 'settings', { show: true }),
      ],
    });
  }

  const moduleOpts = {
    walletUrl: WALLET_URL,
    billingUrl: BILLING_URL,
    marketUrl: MARKET_MODULE_URL,
  };

  return hubs
    .map((hub) => ({
      ...hub,
      items: hub.items.filter((it) => {
        if (!it.href || it.show === false) return false;
        return isHrefVisibleWithActiveModules(it.href, activeModules, moduleOpts);
      }),
      defaultQuickPicks: (hub.defaultQuickPicks || []).filter(Boolean),
    }))
    .filter((hub) => hub.items.length > 0);
}

/**
 * Применяет пресет + user overrides к списку хабов.
 * @param {NavHub[]} hubs
 * @param {object} prefs
 */
export function applyNavigationPreferences(hubs, prefs = {}) {
  const {
    activePresetId = DEFAULT_NAV_PRESET_ID,
    hubOrder = [],
    hiddenHubIds = [],
    hubOverrides = {},
    personalPresets = [],
  } = prefs;

  let allowedHubIds = null;
  let effectiveOrder = Array.isArray(hubOrder) ? hubOrder : [];
  let effectiveHidden = Array.isArray(hiddenHubIds) ? hiddenHubIds : [];
  let effectiveOverrides =
    hubOverrides && typeof hubOverrides === 'object' && !Array.isArray(hubOverrides)
      ? hubOverrides
      : {};

  if (typeof activePresetId === 'string' && activePresetId.startsWith('personal:')) {
    const personalId = activePresetId.slice('personal:'.length);
    const personal = (personalPresets || []).find((p) => p.id === personalId);
    if (personal) {
      if (personal.hubIds?.length) allowedHubIds = new Set(personal.hubIds);
      // Snapshot из personal имеет приоритет, если в prefs ещё пусто (старые сессии)
      if (!effectiveOrder.length && Array.isArray(personal.hubOrder) && personal.hubOrder.length) {
        effectiveOrder = personal.hubOrder;
      }
      if (!effectiveHidden.length && Array.isArray(personal.hiddenHubIds) && personal.hiddenHubIds.length) {
        effectiveHidden = personal.hiddenHubIds;
      }
      if (
        Object.keys(effectiveOverrides).length === 0 &&
        personal.hubOverrides &&
        typeof personal.hubOverrides === 'object'
      ) {
        effectiveOverrides = personal.hubOverrides;
      }
    }
  } else {
    const platformPresets = resolveSystemNavPresets(prefs.platform);
    const system = platformPresets
      ? platformPresets.find((p) => p.id === activePresetId) || platformPresets[0]
      : getSystemPreset(activePresetId);
    if (system?.id !== 'full' && system?.hubIds?.length) {
      allowedHubIds = new Set(system.hubIds);
    }
  }

  const hidden = new Set(effectiveHidden);
  let next = hubs.filter((h) => !hidden.has(h.id));
  if (allowedHubIds) {
    next = next.filter((h) => allowedHubIds.has(h.id));
  }

  if (effectiveOrder.length > 0) {
    const orderMap = new Map(effectiveOrder.map((id, idx) => [id, idx]));
    next = [...next].sort((a, b) => {
      const ai = orderMap.has(a.id) ? orderMap.get(a.id) : 1000;
      const bi = orderMap.has(b.id) ? orderMap.get(b.id) : 1000;
      return ai - bi;
    });
  }

  return next.map((hub) => {
    const ov = effectiveOverrides?.[hub.id];
    if (!ov || typeof ov !== 'object') {
      return { ...hub, userQuickPicks: null };
    }

    const hiddenKeys = new Set(ov.hiddenItemKeys || []);
    const baseKeySet = new Set((hub.items || []).map((it) => it.key));
    let items = (hub.items || []).filter((it) => !hiddenKeys.has(it.key));

    // Foreign / reassigned items merged into this hub via override.extraItems
    const extraItems = Array.isArray(ov.extraItems) ? ov.extraItems : [];
    for (const extra of extraItems) {
      if (!extra || !extra.key || hiddenKeys.has(extra.key) || baseKeySet.has(extra.key)) continue;
      if (items.some((it) => it.key === extra.key)) continue;
      items.push({
        key: extra.key,
        href: extra.href,
        label: extra.label,
        iconKey: extra.iconKey,
        show: extra.show !== false,
        disabled: Boolean(extra.disabled),
        external: Boolean(extra.external),
        badge: extra.badge === 'new' || extra.badge === 'hot' ? extra.badge : undefined,
      });
    }

    if (Array.isArray(ov.itemOrder) && ov.itemOrder.length > 0) {
      const orderMap = new Map(ov.itemOrder.map((k, idx) => [k, idx]));
      items = [...items].sort((a, b) => {
        const ai = orderMap.has(a.key) ? orderMap.get(a.key) : 1000;
        const bi = orderMap.has(b.key) ? orderMap.get(b.key) : 1000;
        return ai - bi;
      });
    }

    // User override only when quickPicks key is present (including []). Never overwrite registry defaults.
    // Uncapped — favorites_only dock shows all; always_first_3 truncates at render time.
    const userQuickPicks = Array.isArray(ov.quickPicks)
      ? ov.quickPicks.filter(Boolean)
      : null;

    return {
      ...hub,
      items,
      defaultQuickPicks: hub.defaultQuickPicks,
      userQuickPicks,
    };
  });
}

export function flattenNavigationItems(hubs) {
  const out = [];
  (hubs || []).forEach((hub) => {
    (hub.items || []).forEach((it) => {
      out.push({
        ...it,
        hubId: hub.id,
        hubLabel: hub.label,
        path: it.href,
        title: it.label,
        available: it.show !== false && !it.disabled,
      });
    });
  });
  return out;
}

export function findHubById(hubs, hubId) {
  return (hubs || []).find((h) => h.id === hubId) || null;
}

export function findHubForHref(hubs, href) {
  if (!href) return null;
  const path = String(href).split('?')[0];
  for (const hub of hubs || []) {
    for (const it of hub.items || []) {
      const itemPath = String(it.href || '').split('?')[0];
      if (it.href === href || itemPath === path || (path && itemPath && path.startsWith(`${itemPath}/`))) {
        return hub;
      }
    }
  }
  return null;
}

export function findItemForHref(hubs, href) {
  if (!href) return null;
  const path = String(href).split('?')[0];
  let best = null;
  let bestLen = -1;
  for (const hub of hubs || []) {
    for (const it of hub.items || []) {
      if (it.external) continue;
      const itemPath = String(it.href || '').split('?')[0];
      if (!itemPath) continue;
      if (it.href === href || it.key === href) {
        return { hub, item: it };
      }
      if (path === itemPath || path.startsWith(`${itemPath}/`)) {
        if (itemPath.length > bestLen) {
          bestLen = itemPath.length;
          best = { hub, item: it };
        }
      }
    }
  }
  return best;
}

/**
 * Выбор активного ключа меню по location.
 */
export function matchSelectedKey(pathname, search = '', hubs = []) {
  const path = pathname || '/';
  const qs = search || '';
  const full = `${path}${qs}`;

  // Query-aware exact keys first
  for (const hub of hubs) {
    for (const it of hub.items || []) {
      if (it.external) continue;
      if (it.href === full || it.key === full) return it.key;
    }
  }

  const found = findItemForHref(hubs, path);
  if (found?.item) return found.item.key;

  // Footer / specials
  if (path.startsWith('/settings/profile')) return '/settings/profile';
  if (path.startsWith('/settings')) return '/settings';
  return path;
}

export function isHubActive(hub, selectedKey) {
  if (!hub || !selectedKey) return false;
  const keyPath = String(selectedKey).split('?')[0];
  return (hub.items || []).some((it) => {
    if (it.key === selectedKey || it.href === selectedKey) return true;
    const itemPath = String(it.href || '').split('?')[0];
    return itemPath && (keyPath === itemPath || keyPath.startsWith(`${itemPath}/`));
  });
}

/**
 * Resolve dock quick-pick items for a hub.
 * - hidden → []
 * - favorites_only → all user favorites that belong to this hub (globalFavoriteKeys or userQuickPicks); no max-3
 * - always_first_3 → up to 3: userQuickPicks → registry defaultQuickPicks → first 3 visible
 *
 * @param {object} hub
 * @param {object} opts
 * @param {string} opts.mode
 * @param {string[]|Set<string>|null} opts.globalFavoriteKeys — SSOT from user_menu_favorites
 */
export function getHubQuickPickItems(hub, { mode = 'always_first_3', globalFavoriteKeys = null } = {}) {
  if (!hub || mode === 'hidden') return [];

  const byKey = new Map((hub.items || []).map((it) => [it.key, it]));
  const byHref = new Map((hub.items || []).map((it) => [it.href, it]));

  const resolvePicks = (picks, max = Infinity) => {
    const result = [];
    for (const pick of picks || []) {
      const found = byKey.get(pick) || byHref.get(pick);
      if (found && !found.disabled) result.push(found);
      if (result.length >= max) break;
    }
    return result;
  };

  const globalList = globalFavoriteKeys
    ? (Array.isArray(globalFavoriteKeys) ? globalFavoriteKeys : [...globalFavoriteKeys])
    : null;

  if (mode === 'favorites_only') {
    // Prefer live menu favorites SSOT; fall back to hub override
    if (globalList) {
      return resolvePicks(globalList);
    }
    if (!Array.isArray(hub.userQuickPicks)) return [];
    return resolvePicks(hub.userQuickPicks);
  }

  // always_first_3 — popular / capped strip
  if (Array.isArray(hub.userQuickPicks) && hub.userQuickPicks.length > 0) {
    return resolvePicks(hub.userQuickPicks, 3);
  }
  if (globalList && globalList.length > 0) {
    const fromFav = resolvePicks(globalList, 3);
    if (fromFav.length > 0) return fromFav;
  }
  const fromDefaults = resolvePicks(hub.defaultQuickPicks || [], 3);
  if (fromDefaults.length > 0) return fromDefaults;
  return (hub.items || []).filter((it) => !it.disabled).slice(0, 3);
}

export const DOCK_DISPLAY_MODES = [
  { id: 'always_first_3', label: 'До 3 популярных', description: 'При наведении — до трёх пунктов (избранные или популярные)' },
  { id: 'favorites_only', label: 'Все избранные', description: 'При наведении — все ★ этого раздела; без лимита 3' },
  { id: 'hidden', label: 'Ничего не показывать', description: 'Без hover-подменю; клик открывает полный список' },
];

/**
 * Merge platform HOT/NEW keys + optional registry item.badge.
 * HOT/NEW are platform/admin marks only — never from user favorites ★.
 * User favorites get `isFavorite: true` for a quiet star in UI (not HOT badge).
 */
export function applyNavigationBadges(hubs, platform = {}) {
  const hot = new Set(platform.hotKeys || []);
  const neu = new Set(platform.newKeys || []);
  return (hubs || []).map((hub) => {
    const favorites = new Set(
      Array.isArray(hub.userQuickPicks) ? hub.userQuickPicks.filter(Boolean) : []
    );
    return {
      ...hub,
      items: (hub.items || []).map((it) => {
        let badge = null;
        if (hot.has(it.key) || hot.has(it.href)) {
          badge = 'hot';
        } else if (neu.has(it.key) || neu.has(it.href)) {
          badge = 'new';
        } else if (it.badge === 'hot' || it.badge === 'new') {
          badge = it.badge;
        }
        const isFavorite = favorites.has(it.key) || favorites.has(it.href);
        return { ...it, badge: badge || null, isFavorite };
      }),
    };
  });
}

/**
 * Resolve system presets: platform override list or FE defaults.
 */
export function resolveSystemNavPresets(platform = {}) {
  if (Array.isArray(platform.systemPresets) && platform.systemPresets.length > 0) {
    return platform.systemPresets;
  }
  return null; // caller uses NAVIGATION_SYSTEM_PRESETS
}

export default buildNavigationHubs;
